import http, { httpMultipart } from "./http";

export interface SidenavItem {
  id: number;
  label: string;
  icon: string | null;
  icon_type?: string | null;
  icon_name?: string | null;
  icon_color?: string | null;
  icon_size?: number | null;
  icon_style?: 'solid' | 'gradient' | string | null;
  icon_gradient?: Record<string, unknown> | null;
  route: string;
  iframe_url: string | null;
  category: string;
  section_id: number | null;
  parent_id?: number | null;
  order: number;
  is_active: boolean;
  item_type: string;
  is_draft: boolean;
  is_system: boolean;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  section?: SidenavSection | null;
  parent?: SidenavItem | null;
  children?: SidenavItem[];
}

export interface SidenavSection {
  id: number;
  name: string;
  label: string;
  order: number;
  is_system: boolean;
  is_active: boolean;
  items?: SidenavItem[];
}

export interface SidenavFilters {
  category?: string;
  section_id?: number;
  status?: "active" | "inactive" | "all";
  item_type?: string;
  search?: string;
}

export interface ReorderItem {
  id: number;
  order: number;
  section_id?: number;
  parent_id?: number | null;
}

export interface ReorderSection {
  id: number;
  order: number;
}

export const getSidenavItems = async (filters?: SidenavFilters) => {
  const params = new URLSearchParams();
  if (filters?.category && filters.category !== "all") {
    params.append("category", filters.category);
  }
  if (filters?.section_id) {
    params.append("section_id", String(filters.section_id));
  }
  if (filters?.status && filters.status !== "all") {
    params.append("status", filters.status);
  }
  if (filters?.item_type) {
    params.append("item_type", filters.item_type);
  }
  if (filters?.search) {
    params.append("search", filters.search);
  }
  const queryString = params.toString();
  const url = queryString
    ? `/api/sidenav-items?${queryString}`
    : "/api/sidenav-items";
  const response = await http.get<SidenavItem[]>(url);
  return response.data;
};

export const getSidenavCategories = async () => {
  const response = await http.get<{
    categories: SidenavSection[];
    item_types: string[];
  }>("/api/sidenav-items-categories");
  return response.data;
};

// Section endpoints
export const getSidenavSections = async () => {
  const response = await http.get<SidenavSection[]>("/api/sidenav-sections");
  return response.data;
};

export const createSidenavSection = async (data: Partial<SidenavSection>) => {
  const response = await http.post<SidenavSection>(
    "/api/sidenav-sections",
    data
  );
  return response.data;
};

export const updateSidenavSection = async (
  id: number,
  data: Partial<SidenavSection>
) => {
  const response = await http.patch<SidenavSection>(
    `/api/sidenav-sections/${id}`,
    data
  );
  return response.data;
};

export const deleteSidenavSection = async (id: number) => {
  await http.delete(`/api/sidenav-sections/${id}`);
};

export const reorderSidenavSections = async (sections: ReorderSection[]) => {
  await http.post("/api/sidenav-sections/reorder", { sections });
};

export const getSidenavItem = async (id: number) => {
  const response = await http.get<SidenavItem>(`/api/sidenav-items/${id}`);
  return response.data;
};

export const createSidenavItem = async (data: FormData) => {
  const response = await httpMultipart.post<SidenavItem>(
    "/api/sidenav-items",
    data
  );
  return response.data;
};

export const updateSidenavItem = async (
  id: number,
  data: Partial<SidenavItem> | FormData
) => {
  if (data instanceof FormData) {
    data.append("_method", "PATCH");
    const response = await httpMultipart.post<SidenavItem>(
      `/api/sidenav-items/${id}`,
      data
    );
    return response.data;
  }
  const response = await http.patch<SidenavItem>(
    `/api/sidenav-items/${id}`,
    data
  );
  return response.data;
};

export const deleteSidenavItem = async (id: number) => {
  await http.delete(`/api/sidenav-items/${id}`);
};

export const toggleSidenavItemStatus = async (id: number) => {
  const response = await http.patch<SidenavItem>(
    `/api/sidenav-items/${id}/toggle-status`
  );
  return response.data;
};

export const duplicateSidenavItem = async (id: number) => {
  const response = await http.post<SidenavItem>(
    `/api/sidenav-items/${id}/duplicate`
  );
  return response.data;
};

export const reorderSidenavItems = async (items: ReorderItem[]) => {
  await http.patch("/api/sidenav-items/reorder", { items });
};

export const bulkDeleteSidenavItems = async (ids: number[]) => {
  await http.post("/api/sidenav-items/bulk-delete", { ids });
};

export const bulkToggleSidenavItemsStatus = async (
  ids: number[],
  is_active: boolean
) => {
  await http.post("/api/sidenav-items/bulk-toggle-status", { ids, is_active });
};
